<script lang="ts" setup>
import { Suggestion } from "~/components/Suggestions/Categories.props"
import { mapProductsAlgolia, ProductAlgolia } from "~/utils/mapProducts"
import { checkEnvSite } from "~/server/utils/getAlgoliaConfig"
import { buildExcludeFilter } from "~/utils/algolia"
import type { WithContext, ItemList } from "schema-dts"
import { Hit, BaseHit } from "@nuxtjs/algolia"
import { ListingType } from "~/utils/listing/listing"

const { params } = useRoute()

const { pageInfos, filters, filtersSlug } = usePageInfo()

const haveSubCategories = ref(false)
const categories = ref()

const indexName: string = `TOP_PRODUCTS${checkEnvSite}`

const indexCategoriesName = useAlgoliaSearch(`TOP_CATEGORIES${checkEnvSite}`)
const isBrandListing = pageInfos?.value?.listingType === ListingType.brand
const hasListingTitle = !isBrandListing

categories.value = filtersSlug.value
  ? await indexCategoriesName
      .search({
        query: "",
        requestOptions: {
          hitsPerPage: 50,
          distinct: true,
          filters: `parentCategory.slug:${filtersSlug.value}`,
          clickAnalytics: true
        }
      })
      .then((response) => {
        haveSubCategories.value = response.hits.length > 0
        return response.hits
      })
  : null

const getSuggestionsCategoriesButtons = (items: any[]): Suggestion[] => {
  let orderedCategories = _orderBy(items, "slug").map(
    (item: any): Suggestion => ({
      text: item.categoryName,
      path: `c-${item.slug}` // TODO: path with accented chars in slug is not working
    })
  )
  return orderedCategories
}

const formatProducts = (items: ProductAlgolia[]) => {
  return mapProductsAlgolia(items)
}

const sortingItems = ref([
  {
    value: `TOP_PRODUCTS${checkEnvSite}`,
    label: "Miglior risultato"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_discount-desc`,
    label: "Sconto decrescente"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_name-asc`,
    label: "Nome prodotto A-Z"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_name-desc`,
    label: "Nome prodotto Z-A"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_price-asc`,
    label: "Prezzo crescente"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_price-desc`,
    label: "Prezzo decrescente"
  }
])

const client = useAlgoliaRef()
const stateMapping = useStateMapping()
const algoliaRouter = useCustomRouting()

const configuration = ref({
  indexName: indexName,
  insights: true,
  routing: {
    router: algoliaRouter.value.router,
    stateMapping: stateMapping(indexName)
  },
  searchClient: client
})

const page = computed(() =>
  params?.page ? parseInt(params.page as string) - 1 : 0
)

const widgets = ref([
  useAisHits({
    // @ts-ignore
    transformItems: (items) => formatProducts(items)
  }),
  useAisConfigure({
    searchParameters: {
      query: pageInfos.value.query,
      hitsPerPage: 40,
      page: page.value,
      distinct: true,
      clickAnalytics: true,
      filters: filters.value,
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    }
  }),
  useAisSearchBox({}),
  useAisStats({}),
  useAisSortBy({
    items: sortingItems.value
  }),
  useAisClearRefinements({}, "all"),
  useAisClearRefinements(
    {
      includedAttributes: ["price.amount_float"]
    },
    "price.amount_float"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["brand.name"]
    },
    "brand.name"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["mainCategory.name"]
    },
    "mainCategory.name"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["inStock", "deductibleProduct"]
    },
    "availability"
  ),
  useAisRefinementList({
    attribute: "brand.name"
  }),
  useAisCurrentRefinements({}, "all"),
  useAisCurrentRefinements(
    {
      includedAttributes: ["brand.name"]
    },
    "brand.name"
  ),
  useAisCurrentRefinements(
    {
      includedAttributes: ["mainCategory.name"]
    },
    "mainCategory.name"
  ),
  useAisRangeInput({
    attribute: "price.amount_float"
  }),
  useAisRefinementList({
    attribute: "mainCategory.name"
  }),
  useAisToggleRefinement({
    attribute: "isDiscounted"
  }),
  useAisToggleRefinement({
    attribute: "inStock"
  }),
  useAisToggleRefinement({
    attribute: "deductibleProduct"
  })
])

const itemList: Ref<Hit<BaseHit>[] | null> = ref(null)

useNuxtApp().hook("app:rendered", () => {
  if (process.server && !!itemList.value) {
    const baseUrl = useRuntimeConfig().public.BASE_URL_APP
    const jsonLd: ComputedRef<WithContext<ItemList>> = computed(() => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      numberOfItems: itemList.value?.length,
      itemListElement: itemList.value?.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        image: item.productImage?.image?.url,
        url: baseUrl + item.path,
        name: item.title
      }))
    }))

    useHead({
      script: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd.value)
        }
      ]
    })
  }
})
</script>

<template>
  <div>
    <AisInstantSearch
      v-if="widgets && configuration"
      :widgets="widgets"
      :configuration="configuration"
    >
      <div
        v-if="
          pageInfos?.listingType ===
          'category' /*&&  categories && categories.length > 0*/
        "
      >
        <SuggestionsCategories
          class="show-no-script"
          :title="`${pageInfos?.mainTitle ?? pageInfos?.categoryName}`"
          :info-text="`${pageInfos?.infoText ?? ''}`"
          :suggestions="getSuggestionsCategoriesButtons(categories)"
        />
      </div>
      <slot name="content" />
      <div class="grid-standard mt-4 md:pt-6" id="algolia-plp-filters">
        <h3
          v-if="hasListingTitle"
          class="elephant-bold col-span-full hidden md:mb-6 md:block"
        >
          {{ formatNameWithoutBackSlash(pageInfos?.pageTitle!) }}
        </h3>

        <AlgoliaPlpFilters
          :show-query="pageInfos?.listingType !== 'brandCategory'"
          :hasResultsNumberUnderFilters="isBrandListing"
        />
      </div>

      <AisHits>
        <template #default="{ items }">
          {{ (itemList = items) && "" }}
          <div v-if="items.length === 0">
            <AlgoliaNoResults />
          </div>
          <div v-else>
            <slot name="products" :items="items" :nbHits="0" />
          </div>
        </template>
      </AisHits>
    </AisInstantSearch>
  </div>
</template>
